import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useFormContext } from 'react-hook-form';
import { colorPalette } from '@repo/mui-config/color-palette';
import type { FormNotifications } from '@repo/shared/types';
import { useIsMobile } from '@repo/mui-config/hooks';
import { ConfigType } from '@repo/api/endpoints/merchant';
import { Button, Modal, ModalHeader } from '@repo/ui';

import { MODAL_STATE } from '@/components/modal/modal.store';
import { useModalWithLocalFallback } from '@/components/modal/use-modal-with-local-fallback';
import { ViewType, type ViewTypes } from '@/store/view.type';
import { RecipientDetailsModal } from '@/components/loan-form/recipient-details-modal';

import { FieldNotification } from './recipient-notification/field-notification';
import { isDocumentNumberError } from '../helpers/is-document-number-error';
import { isPeselNumberError } from '../helpers/is-pesel-number-error';
import type { LoanFormInputFields } from '../schema/loan-form.schema';
import { RecipientDetailsList } from './recipient-details-list/recipient-details-list';

interface LoanRecipientProps {
  providerConfig: ConfigType;
  viewType?: Exclude<ViewType, ViewTypes['APP_LOADING'] | ViewTypes['SERVICE_NOT_AVAILABLE']>;
}

export function LoanRecipient({ providerConfig, viewType }: LoanRecipientProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { open, isLocalModalOpen, closeLocalModal } = useModalWithLocalFallback();
  const context = useFormContext<LoanFormInputFields>();

  const [notificationType, setNotificationType] = useState<FormNotifications>('default');

  const isPhoneError = Boolean(context.formState.errors.phone);
  const isMailError = Boolean(context.formState.errors.mail);
  const isDocumentError = isDocumentNumberError(context.getValues('documentId'));
  const isPeselError = isPeselNumberError(context.getValues('pesel'));

  const isFieldEmpty = useCallback(
    (field: 'mail' | 'phone') => {
      return isEmpty(context.formState.defaultValues?.[field]);
    },
    [context.formState.defaultValues],
  );

  // useEffect for setting informationType
  useEffect(() => {
    let notificationTypeToSet: FormNotifications = 'default';
    if (isFieldEmpty('phone') || isFieldEmpty('mail')) {
      notificationTypeToSet = 'warning';
    }

    if (isPhoneError || isMailError || isDocumentError || isPeselError) {
      notificationTypeToSet = 'error';
    }
    setNotificationType(notificationTypeToSet);
  }, [isFieldEmpty, isMailError, isPhoneError, isDocumentError, isPeselError]);

  return (
    <>
      <Grid container>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack alignItems="center" direction="row">
            <Typography component="p" variant="h4">
              {t('loanFormPage.borrowerDetails.title')}
            </Typography>
            <Button
              onClick={() => {
                open(MODAL_STATE.LOAN_RECIPIENT_DETAILS, {
                  additionalProps: { viewType },
                });
              }}
            >
              <HelpOutlineIcon
                sx={{
                  color: colorPalette.ink.main,
                }}
              />
            </Button>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }} position="relative">
          <Box
            sx={
              !isMobile
                ? {
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    transform: 'translateY(-50%)',
                  }
                : undefined
            }
          >
            <FieldNotification
              notification={notificationType}
              isDocumentError={isDocumentError}
              isPeselError={isPeselError}
              isPhoneError={isPhoneError}
              isMailError={isMailError}
              isMailFieldEmpty={isFieldEmpty('mail')}
              isPhoneFieldEmpty={isFieldEmpty('phone')}
            />
          </Box>
        </Grid>
      </Grid>
      <RecipientDetailsList config={providerConfig} viewType={viewType} />
      <Modal
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              position: 'absolute',
            },
          },
        }}
        isOpen={isLocalModalOpen}
        maxWidth="xs"
      >
        <Box sx={{ pt: 6, pb: 4, px: isMobile ? 2 : 4 }}>
          <ModalHeader
            centeredLogo={false}
            closeButton={{ isVisible: true, onClick: closeLocalModal }}
          />
          <RecipientDetailsModal viewType={viewType} />
        </Box>
      </Modal>
    </>
  );
}
